import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  Switch,
  Row,
  Col,
  Progress,
  Tag,
  message,
  Statistic,
  Timeline,
  Divider,
} from 'antd';
import {
  EnvironmentOutlined,
  CompassOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  MessageOutlined,
  CarOutlined,
  HomeOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { deliveryService } from '../services/deliveryService';

const { Title, Paragraph, Text } = Typography;

const LocationTracking = () => {
  // Load persisted tracking settings on component mount
  const [isTracking, setIsTracking] = useState(() => {
    const savedTracking = localStorage.getItem('deliveryTracking');
    return savedTracking ? JSON.parse(savedTracking) : false;
  });

  // Load persisted location settings
  const [autoShare, setAutoShare] = useState(() => {
    const savedAutoShare = localStorage.getItem('deliveryAutoShare');
    return savedAutoShare ? JSON.parse(savedAutoShare) : true;
  });

  const [backgroundTracking, setBackgroundTracking] = useState(() => {
    const savedBackground = localStorage.getItem('deliveryBackgroundTracking');
    return savedBackground ? JSON.parse(savedBackground) : true;
  });

  const [highAccuracyMode, setHighAccuracyMode] = useState(() => {
    const savedHighAccuracy = localStorage.getItem('deliveryHighAccuracyMode');
    return savedHighAccuracy ? JSON.parse(savedHighAccuracy) : false;
  });

  const [batteryOptimization, setBatteryOptimization] = useState(() => {
    const savedBattery = localStorage.getItem('deliveryBatteryOptimization');
    return savedBattery ? JSON.parse(savedBattery) : false;
  });

  const [currentOrder, setCurrentOrder] = useState(null);
  const [location, setLocation] = useState({
    latitude: 27.7172, // Default: Thamel
    longitude: 85.324, // Default: Kathmandu
    address: '123 Main St, Current City',
    accuracy: 10,
  });
  const [trackingHistory] = useState([]);

  // Load persisted location on component mount
  useEffect(() => {
    const persistedLocation = localStorage.getItem('deliveryLocation');
    if (persistedLocation) {
      try {
        const locationData = JSON.parse(persistedLocation);
        setLocation(locationData);
      } catch (e) {
        console.error('Error parsing persisted location:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Fetch current order and tracking data
    const fetchOrderData = async () => {
      try {
        // Check if user is authenticated first
        const token = localStorage.getItem('delivery_token');
        if (!token) {
          return;
        }

        const response = await deliveryService.getCurrentLocation();
        if (response.data.success) {
          const locationData = response.data.data;

          // Store location in localStorage for persistence
          localStorage.setItem('deliveryLocation', JSON.stringify(locationData));
          setLocation({
            latitude: locationData.latitude || 27.7172,
            longitude: locationData.longitude || 85.324,
            address: locationData.address || 'Location unavailable',
            accuracy: locationData.accuracy ?? 10,
          });
        }

        // Get current order if we have one assigned
        const ordersResponse = await deliveryService.getOrders({ status: 'out_for_delivery' });
        if (ordersResponse.data.success && ordersResponse.data.data.length > 0) {
          setCurrentOrder(ordersResponse.data.data[0]);
        }
      } catch (error) {
        console.error('Error fetching order data:', error);
        // If authentication error, use fallback location
        if (error.response?.status === 401) {
          const fallbackLocation = {
            latitude: 27.7172,
            longitude: 85.324,
            address: '123 Main St, Kathmandu',
            accuracy: 10,
            is_live: false,
            tracking_status: 'offline',
          };
          setLocation(fallbackLocation);
          localStorage.setItem('deliveryLocation', JSON.stringify(fallbackLocation));
        }
      }
    };

    // Load persisted location on component mount
    const persistedLocation = localStorage.getItem('deliveryLocation');
    if (persistedLocation) {
      try {
        const locationData = JSON.parse(persistedLocation);
        setLocation(locationData);
      } catch (e) {
        console.error('Error parsing persisted location:', e);
      }
    }

    fetchOrderData();
  }, []);

  // Poll for real location from backend
  useEffect(() => {
    let interval;
    if (isTracking) {
      interval = setInterval(async () => {
        try {
          const response = await deliveryService.getCurrentLocation();
          if (response.data.success) {
            const locationData = response.data.data;
            localStorage.setItem('deliveryLocation', JSON.stringify(locationData));
            setLocation((prev) => ({
              ...prev,
              latitude: locationData.latitude,
              longitude: locationData.longitude,
              address: locationData.address || prev.address,
              accuracy: locationData.accuracy ?? prev.accuracy,
            }));
          }
        } catch (error) {
          console.error('Error updating location:', error);
        }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isTracking]);

  const startTracking = () => {
    setIsTracking(true);
    localStorage.setItem('deliveryTracking', 'true');
    message.success('Location tracking started');
  };

  const stopTracking = () => {
    setIsTracking(false);
    localStorage.setItem('deliveryTracking', 'false');
    message.info('Tracking stopped');
  };

  const toggleAutoShare = (checked) => {
    setAutoShare(checked);
    localStorage.setItem('deliveryAutoShare', checked.toString());
    message.success(checked ? 'Auto-share enabled' : 'Auto-share disabled');
  };

  const toggleBackgroundTracking = (checked) => {
    setBackgroundTracking(checked);
    localStorage.setItem('deliveryBackgroundTracking', checked.toString());
    message.success(checked ? 'Background tracking enabled' : 'Background tracking disabled');
  };

  const toggleHighAccuracyMode = (checked) => {
    setHighAccuracyMode(checked);
    localStorage.setItem('deliveryHighAccuracyMode', checked.toString());
    message.success(checked ? 'High accuracy mode enabled' : 'High accuracy mode disabled');
  };

  const toggleBatteryOptimization = (checked) => {
    setBatteryOptimization(checked);
    localStorage.setItem('deliveryBatteryOptimization', checked.toString());
    message.success(checked ? 'Battery optimization enabled' : 'Battery optimization disabled');
  };

  const updateLocation = () => {
    setLocation({
      ...location,
      accuracy: Math.max(10, location.accuracy + Math.random() * 5),
    });
    message.success('Location updated');
  };

  const contactCustomer = (type) => {
    if (type === 'call') {
      message.info(`Calling customer at ${currentOrder.customerPhone}`);
    } else {
      message.info('Opening chat with customer');
    }
  };

  const getLocationAccuracy = (accuracy) => {
    if (accuracy == null) return { color: '#ff4d4f', text: 'No data' };
    if (accuracy <= 5) return { color: '#52c41a', text: 'Excellent' };
    if (accuracy <= 10) return { color: '#faad14', text: 'Good' };
    if (accuracy <= 20) return { color: '#1890ff', text: 'Fair' };
    return { color: '#ff4d4f', text: 'Poor' };
  };

  const accuracy = getLocationAccuracy(location.accuracy);

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Location Tracking</Title>
      <Paragraph style={{ color: '#666', marginBottom: '32px' }}>
        Track your location and share it with customers for real-time delivery updates
      </Paragraph>

      {/* Current Status */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <EnvironmentOutlined
                style={{
                  fontSize: '32px',
                  color: isTracking ? '#52c41a' : '#d9d9d9',
                  marginBottom: '16px',
                }}
              />
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '8px' }}>
                {isTracking ? 'Tracking Active' : 'Tracking Inactive'}
              </div>
              <div style={{ color: '#666', marginBottom: '16px' }}>
                {isTracking ? 'Sharing location with customer' : 'Location sharing disabled'}
              </div>
              <Switch
                checked={isTracking}
                onChange={(checked) => (checked ? startTracking() : stopTracking())}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <CompassOutlined
                style={{
                  fontSize: '32px',
                  color: '#1890ff',
                  marginBottom: '16px',
                }}
              />
              <Statistic
                title="GPS Accuracy"
                value={location.accuracy}
                suffix="m"
                valueStyle={{ color: accuracy.color }}
              />
              <div style={{ marginTop: '8px' }}>
                <Tag color={accuracy.color}>{accuracy.text}</Tag>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <ClockCircleOutlined
                style={{
                  fontSize: '32px',
                  color: '#faad14',
                  marginBottom: '16px',
                }}
              />
              <Statistic title="Last Update" value="Just now" valueStyle={{ color: '#faad14' }} />
              <Button
                type="primary"
                size="small"
                onClick={updateLocation}
                style={{ marginTop: '8px' }}
              >
                Update Now
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Current Order Tracking */}
      {currentOrder && (
        <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
          <Col xs={24} lg={16}>
            <Card title={`Current Delivery - ${currentOrder.id}`}>
              <div style={{ marginBottom: '24px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <div>
                    <Text strong>Delivery Progress</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Progress
                        percent={currentOrder.progress}
                        status="active"
                        strokeColor="#1890ff"
                      />
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1890ff' }}>
                      {currentOrder.distance} km
                    </div>
                    <div style={{ color: '#666' }}>{currentOrder.estimatedTime} min remaining</div>
                  </div>
                </div>
              </div>

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Card size="small" title="Pickup Location">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CarOutlined style={{ color: '#52c41a' }} />
                      <div>
                        <div style={{ fontWeight: 'bold' }}>Pharmacy</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          {currentOrder.pickupAddress}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card size="small" title="Delivery Location">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <HomeOutlined style={{ color: '#1890ff' }} />
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{currentOrder.customerName}</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          {currentOrder.deliveryAddress}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>

              <Divider />

              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Space>
                  <Button icon={<PhoneOutlined />} onClick={() => contactCustomer('call')}>
                    Call Customer
                  </Button>
                  <Button icon={<MessageOutlined />} onClick={() => contactCustomer('message')}>
                    Send Message
                  </Button>
                </Space>
                <Space>
                  <Button icon={<CompassOutlined />}>Get Directions</Button>
                  <Button type="primary" icon={<CheckCircleOutlined />}>
                    Mark Delivered
                  </Button>
                </Space>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Current Location">
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '50%',
                    margin: '0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EnvironmentOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <Text strong>Address:</Text>
                <div style={{ marginTop: '4px' }}>{location.address}</div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <Text strong>Coordinates:</Text>
                <div style={{ marginTop: '4px', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  {parseFloat(location.latitude).toFixed(6)},{' '}
                  {parseFloat(location.longitude).toFixed(6)}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <Text strong>Accuracy:</Text>
                <div style={{ marginTop: '4px' }}>
                  <Tag color={accuracy.color}>
                    {location.accuracy}m ({accuracy.text})
                  </Tag>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button type="primary" icon={<CompassOutlined />} onClick={updateLocation} block>
                  Update Location
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tracking History */}
      <Card title="Tracking History">
        <Timeline>
          {trackingHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <ClockCircleOutlined
                style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }}
              />
              <div style={{ color: '#666' }}>No tracking history available</div>
            </div>
          ) : (
            trackingHistory.map((entry, index) => (
              <Timeline.Item
                key={index}
                color={
                  entry.action.includes('Started')
                    ? 'green'
                    : entry.action.includes('Stopped')
                      ? 'red'
                      : 'blue'
                }
                dot={
                  entry.action.includes('Started') ? (
                    <CheckCircleOutlined />
                  ) : entry.action.includes('Stopped') ? (
                    <WarningOutlined />
                  ) : (
                    <EnvironmentOutlined />
                  )
                }
              >
                <div>
                  <Text strong>{entry.action}</Text>
                  <br />
                  <Text type="secondary">{entry.time}</Text>
                  <br />
                  <Text>{entry.location}</Text>
                </div>
              </Timeline.Item>
            ))
          )}
        </Timeline>
      </Card>

      {/* Location Settings */}
      <Card title="Location Settings" style={{ marginTop: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12}>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Auto-share with customers</Text>
              <div style={{ marginTop: '4px' }}>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  Automatically share location when delivery is in progress
                </Text>
              </div>
            </div>
            <Switch
              checked={autoShare}
              onChange={toggleAutoShare}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Background tracking</Text>
              <div style={{ marginTop: '4px' }}>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  Continue tracking when app is in background
                </Text>
              </div>
            </div>
            <Switch
              checked={backgroundTracking}
              onChange={toggleBackgroundTracking}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>High accuracy mode</Text>
              <div style={{ marginTop: '4px' }}>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  Use GPS for more accurate location (uses more battery)
                </Text>
              </div>
            </div>
            <Switch
              checked={highAccuracyMode}
              onChange={toggleHighAccuracyMode}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Battery optimization</Text>
              <div style={{ marginTop: '4px' }}>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  Reduce tracking frequency to save battery
                </Text>
              </div>
            </div>
            <Switch
              checked={batteryOptimization}
              onChange={toggleBatteryOptimization}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default LocationTracking;
